import * as t from 'io-ts';
import api from 'src/api';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { createGuard } from 'src_common/common/createGuard';
import { MobxMap } from 'src_common/common/mobx-utils/MobxMap';

const data = t.record(
    t.string,
    t.interface({
        properties: t.record(t.string, t.string),
        updatedAt: t.string,
    })
);

const isTranslations = createGuard(data);
type TranslationsContentType = t.TypeOf<typeof data>;

export class SelectLanguageDetails {
    constructor(
        readonly language: string,
        readonly properties: Record<string, string>
    ) {}

    public getUniqueKeys(editedKey: string | null): Array<string> {
        return Object.keys(this.properties).filter((x) => x !== editedKey);
    }
}

export type GroupName = 'site' | 'mobile';

export class TranslationsContentState {
    public readonly getAllTranslation: MobxMap<[string], TranslationsContentType | null>;
    @observable language: string | null = null;
    @observable isDownloading: boolean = false;
    @observable hasTranslations: boolean = false;
    @observable groupName: GroupName = 'site';

    constructor() {
        this.getAllTranslation = new MobxMap(async ([groupName]: [string]): Promise<TranslationsContentType> => {
            const data = await api.get(`/translations/${groupName}/all`);

            if (isTranslations(data)) {
                return data;
            }

            return {};
        });
        makeAutoObservable(this);
    }

    @action setGroupName = (groupName: 'site' | 'mobile'): void => {
        this.groupName = groupName;
    };

    @computed get translations(): TranslationsContentType | null {
        const data = this.getAllTranslation.get([this.groupName]).get();

        if (data.type === 'ready') {
            return data.value;
        }

        return null;
    }

    @computed get selectLanguageDetails(): SelectLanguageDetails | null {
        const translations = this.translations;
        const language = this.language;

        if (translations === null || language === null) {
            return null;
        }

        const selectDetails = translations[language];

        if (selectDetails === undefined) {
            return null;
        }

        return new SelectLanguageDetails(language, selectDetails.properties);
    }

    handleBack = (): void => {
        this.language = null;
    };

    selectLanguage = (language: string | null): void => {
        this.language = language;
    };

    refreshLanguages = async (): Promise<void> => {
        await this.getAllTranslation.get([this.groupName]).refresh();
    };

    @action setTranslationAvailable(): void {
        this.hasTranslations = true;
    }
}
